import React from "react";
import PropTypes from "prop-types";
import { strings } from '../../../localization';
import SelectDropDown from '../../../components/common/SelectDropDown';

import BuildSettings from "../../../components/buildSettings";

import EventFactorSlider from "./Modules/EventFactorSlider";

import {
    SUPPORTED_TIME_PERIOD_TYPES,
    REPORT_BY_TYPES,
    colorForSafetyCategory,
    SUPPORTED_SCORE_CATEGORIES } from './SafetyScore';

const getTimePeriodSelectOptions = (selectedTimePeriodType) => {

    return Object.keys(SUPPORTED_TIME_PERIOD_TYPES)
        .filter(key => BuildSettings.environment() !== "prod" || key !== SUPPORTED_TIME_PERIOD_TYPES.QUARTERLY) // Disable QUARTERLY for prod only
        .map((key) => {
            let label = strings[`time_period_${key.toLowerCase()}`];
            if (!label) {
                label = key;
            };
            return {
                label: label,
                value: key,
                checked: selectedTimePeriodType === key
            };
        });
}

const getEntitiesSelectOptions = (entities) => {

    return entities.map((entity) => {
        return {
            label: entity.name,
            value: entity.id,
            checked: entity.selected
        };
    });
}

export default class SafetyScoreModuleGroupNavigation extends React.Component {

    static propTypes = {

        facialRecognitionSupported: PropTypes.bool.isRequired, // to show or hide drivers/vehicles selection option based on env/account-type support
        facialRecognitionEnabled: PropTypes.bool.isRequired, // to show or hide drivers/vehicles selection option based on account settings

        selectedTimePeriodType: PropTypes.string.isRequired,
        onTimePeriodChange: PropTypes.func.isRequired,

        selectedReportBy: PropTypes.string.isRequired,
        onReportByChange: PropTypes.func.isRequired,

        availableEntities: PropTypes.array.isRequired,
        onEntitiesChange: PropTypes.func.isRequired,

        availableEventsFactors: PropTypes.array.isRequired,
        onEventsFactorsChange: PropTypes.func.isRequired,

        selectedScoreThresholds: PropTypes.object.isRequired,
        onScoreThresholdsChange: PropTypes.func.isRequired,

        stateDivergedFromDefaults: PropTypes.bool,
        stateDivergedFromPreferences: PropTypes.bool,
        stateDivergedFromAppliedQueryState: PropTypes.bool,

        allScoreFactorsAreValid: PropTypes.bool,

        onApply: PropTypes.func.isRequired,
        onSave: PropTypes.func.isRequired,
        onCancel: PropTypes.func.isRequired,
        onRestoreDefaults: PropTypes.func.isRequired
    };

    constructor(props) {

        super(props);

        this.state = {
            eventsFactorsCollapsed: false,
            scoreThresholdsCollapsed: false
        };
    };

    onTimePeriodChange = (updatedOptions) => {
        // For SelectDropDown with singleOnlySelection=true, updatedOptions is the index of the selected item.
        const selectedIndex = updatedOptions;
        const timePeriodOptions = getTimePeriodSelectOptions(this.props.selectedTimePeriodType);
        const selectedOptionValue = timePeriodOptions[selectedIndex].value;

        if (this.props.selectedTimePeriodType !== selectedOptionValue) {
            this.props.onTimePeriodChange(selectedOptionValue);
        }
    };

    onReportByChange = (reportBy) => {
        this.props.onReportByChange(reportBy);
    };

    onEntitiesChange = (changedIndex, forceCheckedValue = undefined) => {
        // onEntitiesChange is triggered on individual items, often as part of an event handler loop of more than one item.
        // "React waits until all code in the EVENT HANDLERS has run before processing your state updates" (https://react.dev/learn/queueing-a-series-of-state-updates)
        // Updating availableEntities in the parent component will be too slow (technically, queued or collapsed into one update)
        // Therefore, the update loop in the event handler of the drop-down component has to complete before the parent component's state/props are updated.
        // So we can't depend on availableEntities being updated quickly enough
        // Therefore, though it seems like an anti-pattern, we will update availableEntities directly and also call props.onEntitiesChange
        // to ensure that the currentEntitiesFromProps have all the individual changes applied to them already.

        const currentEntitiesFromProps = this.props.availableEntities;
        const currentOptionObjects = getEntitiesSelectOptions(currentEntitiesFromProps);

        const isCurrentlyChecked = currentOptionObjects[changedIndex].checked;
        const newSelectedState = forceCheckedValue === undefined ? !isCurrentlyChecked : forceCheckedValue;

        currentEntitiesFromProps.forEach((entity, index) => {
            if (index === changedIndex) {
                entity.selected = newSelectedState;
            }
        });
        this.props.onEntitiesChange(currentEntitiesFromProps);
    };

    onEntitiesSelectAll = () => {
        const updatedEntities = this.props.availableEntities.map((entity) => {
            return {
                ...entity,
                selected: true
            };
        });
        this.props.onEntitiesChange(updatedEntities);
    };

    onEntitiesSelectNone = () => {
        const updatedEntities = this.props.availableEntities.map((entity) => {
            return {
                ...entity,
                selected: false
            };
        });
        this.props.onEntitiesChange(updatedEntities);
    };

    onEventsFactorsChange = (eventsFactors) => {
        this.props.onEventsFactorsChange(eventsFactors);
    };

    onScoreThresholdsChange = (scoreThresholds) => {
        this.setState({ scoreThresholds });
    };

    getValueRangeForScoreThreshold = (scoreCategory) => {

        const highRiskThreshold = 0;
        const mediumRiskThreshold = this.props.selectedScoreThresholds[SUPPORTED_SCORE_CATEGORIES.MEDIUM];
        const mildRiskThreashold = this.props.selectedScoreThresholds[SUPPORTED_SCORE_CATEGORIES.MILD];
        const lowRiskThreshold = this.props.selectedScoreThresholds[SUPPORTED_SCORE_CATEGORIES.LOW];

        let range = [];
        switch (scoreCategory) {
            case SUPPORTED_SCORE_CATEGORIES.HIGH:
                range = [0, mediumRiskThreshold - 5];
                break;
            case SUPPORTED_SCORE_CATEGORIES.MEDIUM:
                range = [highRiskThreshold + 5, mildRiskThreashold - 5];
                break;
            case SUPPORTED_SCORE_CATEGORIES.MILD:
                range = [mediumRiskThreshold + 5, lowRiskThreshold - 5];
                break;
            case SUPPORTED_SCORE_CATEGORIES.LOW:
                range = [mildRiskThreashold + 5, 100];
                break;
            default:
                console.warn("SafetyScoreModuleGroupNavigation getValueRangeForScoreThreshold: Unknown score category", scoreCategory);
                range = [0, 100];
                break;
        }

        const [min, max] = range;
        const multiplesOfFive = [];
        for (let i = Math.ceil(min / 5) * 5; i <= max; i += 5) {
            multiplesOfFive.push(i);
        }
        return multiplesOfFive;
    };

    viewportHeightRequiresScrolling = () => {
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
        if (viewportHeight < 690) {
            return true;
        }
        return false;
    }

    render() {
        const {
            selectedTimePeriodType, 
            selectedReportBy,
            availableEntities,
            availableEventsFactors,
            selectedScoreThresholds
        } = this.props;

        return (
            <div className="score-options" style={{ overflowY: this.viewportHeightRequiresScrolling() ? "auto" : "visible" }}>

                <div className="score-options-period">
                    <div className="score-options-period-label">
                        {strings.score_options_period}
                    </div>
                    <SelectDropDown
                        for="timePeriod"
                        enableFilter={false}
                        options={getTimePeriodSelectOptions(selectedTimePeriodType)}
                        onChange={this.onTimePeriodChange}
                        singleOnlySelection={true}
                    />
                </div>

                {this.props.facialRecognitionSupported && this.props.facialRecognitionEnabled &&
                <div className="score-options-report-by">
                    <div className="score-options-report-by-label">
                        {strings.score_options_report_by}
                    </div>
                    <div className="score-options-report-by-radio-group">
                        <div className="score-options-report-by-radio-wrapper">
                            <input
                                type="radio"
                                id="reportByDriver"
                                name="reportBy"
                                value={REPORT_BY_TYPES.DRIVER}
                                checked={selectedReportBy === REPORT_BY_TYPES.DRIVER}
                                onChange={() => this.onReportByChange(REPORT_BY_TYPES.DRIVER)}
                            />
                            <label htmlFor="reportByDriver">
                                {strings.score_options_report_by_driver}
                            </label>
                        </div>
                        <div className="score-options-report-by-radio-wrapper">
                            <input
                                type="radio"
                                id="reportByVehicle"
                                name="reportBy"
                                value={REPORT_BY_TYPES.VEHICLE}
                                checked={selectedReportBy === REPORT_BY_TYPES.VEHICLE}
                                onChange={() => this.onReportByChange(REPORT_BY_TYPES.VEHICLE)}
                            />
                            <label htmlFor="reportByVehicle">
                                {strings.score_options_report_by_vehicle}
                            </label>
                        </div>
                    </div>
                </div>
                }


                <div className="score-options-entities">

                    <div className="score-options-entities-label">
                        {selectedReportBy === REPORT_BY_TYPES.DRIVER ?
                            strings.score_options_select_entities_drivers
                            :
                            strings.score_options_select_entities_vehicles
                        }
                    </div>

                    <div className="score-options-entities-select-all">
                        <button
                            className="btn btn-sm"
                            onClick={this.onEntitiesSelectAll}
                        >
                            {strings.action_select_all}
                        </button>
                        <button
                            className="btn btn-sm"
                            onClick={this.onEntitiesSelectNone}
                        >
                            {strings.action_select_none}
                        </button>
                    </div>

                    <SelectDropDown
                        for="entities"
                        //nameEmbeddedSelectBox={selectedReportBy === REPORT_BY_TYPES.DRIVER ? strings.score_drivers : strings.score_vehicles}
                        requiredSelection={true}
                        enableFilter={false}
                        enableSearch={true}
                        enableSimplifiedClear={false}
                        options={getEntitiesSelectOptions(availableEntities)}
                        onChange={this.onEntitiesChange}
                        onSetAll={() => { console.warn("SafetyScoreModuleGroupNavigation SelectDropDown triggered onSetAll unexpectedly") }}
                    />

                </div>

                <div className={`score-options-events-factors ${this.state.eventsFactorsCollapsed ? 'collapsed' : ''}`}>
                    <button
                        className="score-options-events-factors-label"
                        onClick={() => this.setState({ eventsFactorsCollapsed: !this.state.eventsFactorsCollapsed })}
                    >
                        <span>{strings.score_options_events_factors}</span>
                        <i className="material-icons disclosure">keyboard_arrow_up</i>
                    </button>
                    <div className="score-options-description">
                        {strings.score_options_events_factors_description}
                    </div>
                    <div className="score-options-events-factors-options">
                        {availableEventsFactors.map((eventFactor, index) => {
                            return (
                                <EventFactorSlider
                                    key={index}
                                    eventFactor={eventFactor}
                                    onChange={(value) => {
                                        const updatedEventsFactors = availableEventsFactors.map((factor, i) => {
                                            if (i === index) {
                                                return {
                                                    ...factor,
                                                    weight: value
                                                };
                                            }
                                            return factor;
                                        });
                                        this.onEventsFactorsChange(updatedEventsFactors);
                                    }}
                                />
                            );
                        })}
                    </div>
                </div>

                <div className={`score-options-score-thresholds ${this.state.scoreThresholdsCollapsed ? 'collapsed' : ''}`}>

                    <button
                        className="score-options-score-thresholds-label"
                        onClick={() => this.setState({ scoreThresholdsCollapsed: !this.state.scoreThresholdsCollapsed })}
                    >
                        <span>{strings.score_options_score_thresholds}</span>
                        <i className="material-icons disclosure">keyboard_arrow_up</i>
                    </button>

                    <div className="score-options-description">
                        {strings.score_options_score_thresholds_description}
                    </div>
                    <div className="score-options-score-thresholds-options">
                        {this.props.selectedScoreThresholds && Object.keys(this.props.selectedScoreThresholds).map((scoreCategory, index) => {
                            if (scoreCategory === SUPPORTED_SCORE_CATEGORIES.HIGH ) {
                                return null;
                            }
                            const scoreThreshold = this.props.selectedScoreThresholds[scoreCategory];
                            return (
                                <div key={index} className="score-options-score-threshold">
                                    
                                    <div className="score-threshold-color" style={{ backgroundColor: colorForSafetyCategory(scoreCategory)}}></div>
                                    <div className="score-threshold-label">
                                        {strings[`score_risk_label_${scoreCategory.toLowerCase()}_compact`]}
                                        {" >"}
                                    </div>
                                    <select
                                        name="score-threshold-select"
                                        className="score-threshold-select"
                                        value={scoreThreshold}
                                        onChange={(e) => {
                                            const updatedScoreThresholds = { ...this.props.selectedScoreThresholds };
                                            updatedScoreThresholds[scoreCategory] = parseInt(e.target.value, 10);
                                            this.props.onScoreThresholdsChange(updatedScoreThresholds);
                                        }}
                                    >
                                        {this.getValueRangeForScoreThreshold(scoreCategory).map((value, index) => {
                                            return (
                                                <option key={index} value={value}>
                                                    {value}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                            );
                        })}
                    </div>

                </div>

                <div className="score-options-main-actions">
                    <div className="score-unsaved-factors-actions">
                        {(this.props.stateDivergedFromDefaults || this.props.stateDivergedFromPreferences) &&
                        <>
                            {this.props.stateDivergedFromPreferences &&
                                <button
                                    className="btn"
                                    onClick={this.props.onSave}
                                    disabled={!this.props.allScoreFactorsAreValid}
                                >
                                    {strings.action_save_custom}

                                </button>
                            }

                            <div className="destructive-actions">
                                {this.props.stateDivergedFromDefaults &&
                                    <button
                                        onClick={this.props.onRestoreDefaults}
                                    >
                                        {strings.action_restore_default}
                                    </button>
                                }
                                {this.props.stateDivergedFromPreferences &&
                                    <button
                                        onClick={this.props.onCancel}
                                    >
                                        {strings.action_undo_changes}
                                    </button>
                                }
                            </div>
                        </>
                        }
                        

                    </div>
                    <div>
                        <button
                            className="btn score-factors-action"
                            onClick={this.props.onApply}
                            disabled={!this.props.allScoreFactorsAreValid || !this.props.stateDivergedFromAppliedQueryState}
                        >
                            {strings.action_load_score}
                        </button>
                    </div>
                </div>

            </div>
        );
    }
}